import type { FsId } from '@fullstory/recording-api';
import type { NormalizedTagName } from '@fullstory/rewriters';
import type { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';

/**
 * The vDOM representation of a {@link DocumentType} node.
 *
 * In reality, the doctype of an {@link FsDocument} is set by a <!DOCTYPE> node
 * injected by the playback service worker, and is fixed at the time when the
 * iframe holding the {@link FsDocument} is created. It's still useful to have a
 * vDOM representation of the node so that we can see what the recording
 * contained, though.
 */
export class FsDocumentType extends FsNode {
  private _name: string | null = null;
  private _publicId: string | null = null;
  private _systemId: string | null = null;

  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, '!doctype' as NormalizedTagName, undefined, fsid);
  }

  setAttr(rawName: string, requestedValue: string | null, _skipRewrite?: boolean): void {
    // We use attributes to encode the special DocumentType properties.
    switch (rawName) {
      case 'name':
        this._name = requestedValue;
        break;

      case 'publicId':
        this._publicId = requestedValue;
        break;

      case 'systemId':
        this._systemId = requestedValue;
        break;

      default:
        console.warn(
          `Setting unsupported attribute on FsDocumentType: ` +
            `'${rawName}' = '${requestedValue}'`,
        );
        break;
    }

    // Note that we don't set any dirty bits here. The properties we have when
    // we get flushed for the first time stick around permanently. We do that
    // for two reasons. First, the properties of a DocumentType node are not
    // mutable once it has been created, so we can't simply mutate the
    // underlying DOM node during a flush. Second, even if we destroyed the DOM
    // node and replaced it with a new one, there's not really any point in
    // doing so, since you can't change the doctype of a document once it has
    // loaded.  (Well, except by using 'document.write()' to rewrite the
    // document from scratch.)
  }

  protected createNode(doc: Document): DocumentType {
    try {
      return doc.implementation.createDocumentType(
        this._name ?? 'html',
        this._publicId ?? '',
        this._systemId ?? '',
      );
    } catch (e) {
      console.warn(
        `Invalid doctype (` +
          `name: '${this._name}', ` +
          `publicId: '${this._publicId}', ` +
          `systemId: '${this._systemId}'` +
          `): ${e}`,
      );
      return doc.implementation.createDocumentType('html', '', '');
    }
  }
}
